import { HighChartBuilder } from '../../builders/highchart.builder';
import { TraceLoggingHelpers } from '../../helpers/trace-logging-helpers';
import { TIDataPointToHighChartDataPointMapper } from '../../mappers/data-point/ti-chart-point-to-highchart-data-point.mapper';
import { RealtimeTradeDataModel } from "../../models/data-point/realtime-trade-data.model";
import { TIChartPointModel } from '../../models/data-point/ti-chart-point.model';
import { IComponent } from "../component.interface";
import { TiChartSeriesComponent } from "../ti-chart-series.component";

export class MainChartComponent implements IComponent {
    public id = '';

    private tiChartSeries: TiChartSeriesComponent;

    private isPnP = false;
    constructor(tiChartSeries: TiChartSeriesComponent) {
        this.tiChartSeries = tiChartSeries;
        this.isPnP = this.tiChartSeries.base.pnpMode;
    }

    public updateRealTimeChartData(data: RealtimeTradeDataModel) {
        if (!this.tiChartSeries.chartStyleSettings || !this.tiChartSeries.lastDataPoint) {
            return;
        }

        if (!this.tiChartSeries.ready) {
            return;
        }

        if (!this.tiChartSeries.isCurrentBarVisible()) {
            return;
        }

        const series: any = this.tiChartSeries.highStockChart.series[0];
        if (data.shouldUpdateExistingBar()) {
            const ind = this.tiChartSeries.tiChartPoints.findIndex((x: TIChartPointModel) => x.xValue == data.xValue);
            let lastChartBar = series.points.find((x: any) => x?.x == data.xValue);
            if (!lastChartBar || ind <-1) {
                return;
            }

            const currentDataPoint = this.tiChartSeries.tiChartPoints[ind];

            currentDataPoint.close = data.close;
            currentDataPoint.open = data.open;
            currentDataPoint.high = data.high;
            currentDataPoint.low = data.low;

            let lastHighChartDataPoint = TIDataPointToHighChartDataPointMapper.toHighChartDataPoint(this.tiChartSeries, currentDataPoint);

            lastChartBar.update(lastHighChartDataPoint, true);

            if (data.xValue == this.tiChartSeries.lastDataPoint.xValue) {
                this.tiChartSeries.lastDataPoint.close = data.close;
                this.tiChartSeries.lastDataPoint.open = data.open;
                this.tiChartSeries.lastDataPoint.high = data.high;
                this.tiChartSeries.lastDataPoint.low = data.low;
            }
        } else {
            if (this.tiChartSeries.lastDataPoint.xValue == data.xValue) {
                return;
            }

            //Note: Commenting out Real Time Trades creating new bars for now, since it can collide with the 10s Refresh, Highcharts seems to crash and we get 2 main chart area bars being created and flat indicator bars being created
            const newTIChartPoint = new TIChartPointModel({
                open: data.open,
                high: data.high,
                low: data.low,
                close: data.close,
                timestamp: data.timestamp,
                volume: 0,
                volumeAvg: 0,
                start: data.start,
                startTimeT: data.startTimeT,
                end: data.end,
                endTimeT: data.endTimeT,
                xValue: data.xValue,
                isPreMarket: data.isPreMarket,
                isPostMarket: data.isPostMarket,
                goLevel: this.tiChartSeries.lastDataPoint.goLevel,
                color: ''
            });

            this.tiChartSeries.tiChartPoints.push(newTIChartPoint);
            this.tiChartSeries.lastDataPoint = newTIChartPoint;

            let highChartDataPoint = TIDataPointToHighChartDataPointMapper.toHighChartDataPoint(this.tiChartSeries, newTIChartPoint);
            series.addPoint(highChartDataPoint, true);

            this.tiChartSeries.calcMinMaxTimeframeFromXValue(data.xValue, true);

            this.tiChartSeries.extreamMax += 1;
            this.tiChartSeries.setExtreamToMinMax();

            let needHighChartZone = false;
            if (this.tiChartSeries.highChartZonePoints.length) {
                needHighChartZone = true;
            }

            if (needHighChartZone) {
                const highChartZoneDataPoint = TIDataPointToHighChartDataPointMapper.toHighChartZone(this.tiChartSeries.chartStyleSettings, newTIChartPoint);
                this.tiChartSeries.highChartZonePoints.push(highChartZoneDataPoint);
                series.update({
                    zones: this.tiChartSeries.highChartZonePoints,
                    zoneAxis: 'x'
                });
            }
        }
    }

    public updateChartData(data: TIChartPointModel) {
        if (!this.tiChartSeries.chartStyleSettings || !this.tiChartSeries.lastDataPoint) {
            return;
        }

        const lastXValue = this.tiChartSeries.lastDataPoint.xValue;
        const series = this.tiChartSeries.highStockChart.series[0];

        if (!this.tiChartSeries.base.marketingWebChartMode) {
            if (!this.tiChartSeries.ready) {
                // update to tiChartPoints
                if (lastXValue <= data.xValue) {
                    const ind = this.tiChartSeries.tiChartPoints.findIndex((x: TIChartPointModel) => x.xValue == data.xValue);
                    if (ind < 0) {
                        return;
                    }
    
                    this.tiChartSeries.tiChartPoints[ind] = data;
                    if (this.tiChartSeries.lastDataPoint.xValue == data.xValue) {
                        this.tiChartSeries.lastDataPoint = data;
                    }
                } else if (data.xValue > lastXValue) {
                    this.tiChartSeries.originalMaximumXValue = data.xValue;
                    this.tiChartSeries.tiChartPoints.push(data);
                    this.tiChartSeries.lastDataPoint = data;
                }
    
                return;
            }
    
            if (lastXValue > data.xValue) {
                return;
            }
        }

        this.tiChartSeries.ready = false;

        let needHighChartZone = false;
        if (this.tiChartSeries.highChartZonePoints.length) {
            needHighChartZone = true;
        }

        if (data.xValue <= lastXValue) {
            const ind = this.tiChartSeries.tiChartPoints.findIndex((x: TIChartPointModel) => x.xValue == data.xValue);
            if (ind < 0) {
                return;
            }

            this.tiChartSeries.tiChartPoints[ind] = data;
            if (this.tiChartSeries.lastDataPoint.xValue == data.xValue) {
                this.tiChartSeries.lastDataPoint = data;
            }

            const dataPoints = TIDataPointToHighChartDataPointMapper.map(this.tiChartSeries);
            series.setData(dataPoints.points, true, false, false);
            if (needHighChartZone) {
                const highChartZoneDataPoint = TIDataPointToHighChartDataPointMapper.toHighChartZone(this.tiChartSeries.chartStyleSettings, data);
                const existingIndex = this.tiChartSeries.highChartZonePoints.findIndex((x: any) => x.value == data.xValue);
                if (existingIndex > -1) {
                    this.tiChartSeries.highChartZonePoints[existingIndex] = highChartZoneDataPoint;
                    this.tiChartSeries.highChartZonePoints = this.tiChartSeries.highChartZonePoints.filter((x: any) => parseInt(x.value, 10) != Number.NaN);
                    this.tiChartSeries.highChartZonePoints.sort((x: any, y: any) => { return x.value - y.value });
                    series.update({
                        zones: this.tiChartSeries.highChartZonePoints,
                        zoneAxis: 'x'
                    } as any);
                }
            }
        } else {
            let highChartDataPoint = TIDataPointToHighChartDataPointMapper.toHighChartDataPoint(this.tiChartSeries, data);
            if (!this.tiChartSeries.isCurrentBarVisible()) {
                this.tiChartSeries.originalMaximumXValue = data.xValue;
                series.addPoint(highChartDataPoint, false);
            } else {
                series.addPoint(highChartDataPoint, true);
                this.tiChartSeries.calcMinMaxTimeframeFromXValue(data.xValue, true);
                
                this.tiChartSeries.extreamMax += 1;
                this.tiChartSeries.setExtreamToMinMax();
            }

            const hiddenSeries = this.tiChartSeries.highStockChart.series.find(x => x.name == HighChartBuilder.mainChartAreaName + '-hidden-series');
            if (hiddenSeries) {
                this.tiChartSeries.lastHiddenPointXValue += 1;
                hiddenSeries.addPoint({
                    x: this.tiChartSeries.lastHiddenPointXValue,
                    y: 0,
                    open: 0,
                    high: 0,
                    low: 0,
                    close: 0
                }, true);

                const point = TIDataPointToHighChartDataPointMapper.toHighChartDataPoint(this.tiChartSeries, data);
                point.x = this.tiChartSeries.lastHiddenPointXValue;
                point.className = 'hidden-point';
                this.tiChartSeries.fakeTiChartPoints.push(point);
            }

            this.tiChartSeries.tiChartPoints.push(data);
            this.tiChartSeries.lastDataPoint = data;

            if (needHighChartZone) {
                const highChartZoneDataPoint = TIDataPointToHighChartDataPointMapper.toHighChartZone(this.tiChartSeries.chartStyleSettings, data);
                this.tiChartSeries.highChartZonePoints.push(highChartZoneDataPoint);
                series.update({
                    zones: this.tiChartSeries.highChartZonePoints,
                    zoneAxis: 'x'
                } as any);
            }

            if (!this.isPnP)
                TraceLoggingHelpers.log('Add new point', data);
        }

        if (!this.tiChartSeries.hasIndicator) {
            this.tiChartSeries.setExtreamToMinMax();
            this.tiChartSeries.setChartReady();
        }
    }

    public updateHistoryChartData(data: Array<TIChartPointModel>) {
        if (!this.tiChartSeries.chartStyleSettings) {
            return;
        }

        this.tiChartSeries.ready = false;
        const series: any = this.tiChartSeries.highStockChart.series[0];
        const firstPoint = series.xData[0];
        let needHighChartZoneColor = false;
        if (this.tiChartSeries.highChartZonePoints.length) {
            needHighChartZoneColor = true;
        }

        for (let i = data.length - 1; i > 0; i--) {
            let exist = false;
            const currentData = data[i];
            for (let j = 0; j < this.tiChartSeries.tiChartPoints.length; j++) {
                const currentMainChartPoint = this.tiChartSeries.tiChartPoints[j];
                if (currentMainChartPoint && currentData && currentMainChartPoint.timestamp == currentData.timestamp) {
                    this.tiChartSeries.tiChartPoints[j] = currentData;
                    exist = true;
                }
            }

            if (!exist && currentData) {
                this.tiChartSeries.tiChartPoints.unshift(currentData);
                if (currentData.timestamp && currentData.xValue < firstPoint) {
                    series.addPoint(TIDataPointToHighChartDataPointMapper.toHighChartDataPoint(this.tiChartSeries, currentData), false);

                    const zoneClassName = currentData.isUpBar() ? 'zone-up-color' : 'zone-down-color';
                    if (needHighChartZoneColor && this.tiChartSeries.highChartZonePoints[0].className != zoneClassName) {
                        this.tiChartSeries.highChartZonePoints.unshift({
                            value: currentData.xValue,
                            color: zoneClassName
                        })
                    }
                }
            }
        }

        if (needHighChartZoneColor) {
            series.update({
                zones: this.tiChartSeries.highChartZonePoints,
                zoneAxis: 'x'
            });
        }

        this.tiChartSeries.firstDataPoint = this.tiChartSeries.tiChartPoints[0];
        if (!this.tiChartSeries.hasIndicator) {
            this.tiChartSeries.setExtremes(this.tiChartSeries.extreamMin, this.tiChartSeries.extreamMax);
            this.tiChartSeries.setChartReady();
        }
    }
}